/**
 * TikTok Pixel — client-side tracking + server-side Events API relay.
 */

import { hasOptedOut } from "./consent";
import { isProductionHost } from "./is-production-host";
const TIKTOK_PIXEL_ID = "D7189TBC77UC2JQLN100";

declare global {
  interface Window {
    ttq: any;
    TiktokAnalyticsObject: string;
  }
}

let pixelLoaded = false;

export function initTikTokPixel() {
  if (pixelLoaded || typeof window === "undefined" || !isProductionHost()) return;
  pixelLoaded = true;

  /* TikTok base pixel code */
  (function (w: any, d: Document, t: string) {
    w.TiktokAnalyticsObject = t;
    const ttq = (w[t] = w[t] || []);
    ttq.methods = [
      "page", "track", "identify", "instances", "debug", "on", "off",
      "once", "ready", "alias", "group", "enableCookie", "disableCookie",
      "holdConsent", "revokeConsent", "grantConsent",
    ];
    ttq.setAndDefer = function (t: any, e: string) {
      t[e] = function (...args: any[]) {
        t.push([e, ...args]);
      };
    };
    for (const method of ttq.methods) {
      ttq.setAndDefer(ttq, method);
    }
    ttq.instance = function (tokenId: string) {
      const instance = ttq._i[tokenId] || [];
      for (const method of ttq.methods) {
        ttq.setAndDefer(instance, method);
      }
      return instance;
    };
    ttq.load = function (tokenId: string, n?: any) {
      const p = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i = ttq._i || {};
      ttq._i[tokenId] = [];
      ttq._i[tokenId]._u = p;
      ttq._t = ttq._t || {};
      ttq._t[tokenId] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[tokenId] = n || {};
      const s = d.createElement("script") as HTMLScriptElement;
      s.type = "text/javascript";
      s.async = true;
      s.src = p + "?sdkid=" + tokenId + "&lib=" + t;
      const first = d.getElementsByTagName("script")[0];
      first?.parentNode?.insertBefore(s, first);
    };
    ttq.load(TIKTOK_PIXEL_ID);
    // US opt-out (CCPA/CPRA): suppress cookies and hold consent before pageview.
    if (hasOptedOut()) {
      try {
        ttq.disableCookie?.();
        ttq.holdConsent?.();
      } catch {
        /* ignore */
      }
    }
    ttq.page();
  })(window, document, "ttq");
}

export function ttqPageView() {
  if (typeof window !== "undefined" && window.ttq) {
    window.ttq.page();
  }
  sendTikTokEvent("Pageview");
}

export function ttqViewContent(params: {
  content_name: string;
  content_id: string;
  content_type: string;
  value?: number;
  currency?: string;
}) {
  if (typeof window !== "undefined" && window.ttq) {
    window.ttq.track("ViewContent", {
      content_name: params.content_name,
      content_id: params.content_id,
      content_type: params.content_type,
      value: params.value,
      currency: params.currency || "USD",
    });
  }
  sendTikTokEvent("ViewContent", params);
}

export function ttqAddToCart(params: {
  content_name: string;
  content_id: string;
  content_type: string;
  value: number;
  currency: string;
  quantity: number;
}) {
  if (typeof window !== "undefined" && window.ttq) {
    window.ttq.track("AddToCart", {
      content_name: params.content_name,
      content_id: params.content_id,
      content_type: params.content_type,
      value: params.value,
      currency: params.currency,
      quantity: params.quantity,
    });
  }
  sendTikTokEvent("AddToCart", params);
}

export function ttqInitiateCheckout(params: {
  content_ids: string[];
  value: number;
  currency: string;
}) {
  if (typeof window !== "undefined" && window.ttq) {
    window.ttq.track("InitiateCheckout", {
      contents: params.content_ids.map((id) => ({ content_id: id, content_type: "product" })),
      value: params.value,
      currency: params.currency,
    });
  }
  sendTikTokEvent("InitiateCheckout", params);
}

// ─── Server-side Events API relay (fire & forget) ───────────────────
function sendTikTokEvent(event: string, properties: Record<string, unknown> = {}) {
  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
  if (!SUPABASE_URL || !isProductionHost()) return;

  const optedOut = hasOptedOut();

  fetch(`${SUPABASE_URL}/functions/v1/tiktok-events-api`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      event,
      event_source_url: window.location.href,
      properties,
      limited_data_use: optedOut,
    }),
  }).catch(() => {
    /* fire-and-forget */
  });
}
